import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { getLoadedSentry, startSentry } from '@/lib/sentryLazy';
import { sentryFeedbackLabels } from '@/lib/sentryFeedbackLabels';
import { useUIStore } from '@/store/uiStore';
import { useToast } from '@/components/ui/ToastProvider';

/**
 * Odpre Sentryjev obrazec povratne informacije.
 *
 * T2410: izpeljano iz `Topbar`, ker ga v urejevalniku popisa ponudi tudi
 * stisnjena glava (`TopbarCompact`) — dve kopiji bi se razšli pri T1748/T2445/
 * T2449, ki so vsi popravljali prav to funkcijo.
 */
export function useOpenFeedback(): () => Promise<void> {
  const { t } = useTranslation();
  const { toast } = useToast();
  // Brez selektorja: testi Topbara zamenjajo store s funkcijo brez selektorja.
  const { isDarkMode } = useUIStore();

  return useCallback(async () => {
    try {
      // T2899: Sentry se naloži po prvem izrisu; klik pred tem nalaganje sproži
      // in počaka nanj. Brez Sentryja (brez DSN, razvoj) modul vseeno uvozimo,
      // da `getFeedback()` vrne prazno in uporabnik dobi obvestilo kot prej.
      const Sentry = getLoadedSentry() ?? (await startSentry()) ?? (await import('@sentry/react'));
      const feedback = Sentry.getFeedback();
      if (!feedback) {
        toast({ type: 'info', title: t('topbar.feedback'), message: t('topbar.feedback_unavailable', { defaultValue: 'Povratna informacija trenutno ni na voljo.' }) });
        return;
      }
      // T1748: tema aplikacije se lahko razlikuje od sistemske, zato ne
      // `'system'`, ampak izrecno trenutna tema. Sentry slog senčnega korena
      // ustvari enkrat in ga drugi `createForm` ne osveži — `setTheme` ga
      // zamenja, tako da obrazec sledi tudi preklopu teme med sejo.
      feedback.setTheme(isDarkMode ? 'dark' : 'light');
      // T2445: Sentry obrazec ob zaprtju ali po oddaji le skrije, iz senčnega
      // korena `#sentry-feedback` pa ga ne odstrani — brez `removeFromDom` je
      // vsako odpiranje dodalo nov `<dialog>`.
      // T2449: oznake iz `Sentry.init` (main.tsx) so v jeziku ob zagonu;
      // `createForm` podane opcije spoji čez privzete (`mergeOptions` v
      // `@sentry-internal/feedback`), zato tu podamo oznake v trenutnem jeziku.
      const form = await feedback.createForm({
        ...sentryFeedbackLabels(t),
        onFormClose: () => form.removeFromDom(),
        onFormSubmitted: () => form.removeFromDom(),
      });
      form.appendToDom();
      form.open();
    } catch (err) {
      console.error('[Feedback] Napaka pri odpiranju forme:', err);
      toast({ type: 'error', title: t('topbar.feedback'), message: t('topbar.feedback_error', { defaultValue: 'Napaka pri odpiranju forme za povratno informacijo.' }) });
    }
  }, [t, toast, isDarkMode]);
}
